import { Pause, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react';

import { CANVAS, TONES, MONO } from './theme';
import { SPEEDS, type Speed } from './usePlayer';

/**
 * The narration for the current step, tagged with the pseudocode line it
 * belongs to. The tag is what ties the sentence to the highlighted line.
 */
export function NarrationBar({ narration, line }: { narration: string; line?: number }) {
  return (
    <div
      className="rounded-2xl border px-4 py-3.5"
      style={{ backgroundColor: CANVAS.panel, borderColor: CANVAS.panelBorder }}
    >
      {/*
        aria-live so screen-reader users hear each step as the animation advances;
        `polite` because interrupting on every frame would be unusable.
      */}
      <p
        className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[14px] leading-relaxed sm:text-[15px]"
        style={{ color: CANVAS.text }}
        aria-live="polite"
      >
        {line !== undefined && (
          <span
            className="shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold"
            style={{ borderColor: `${TONES.accent}66`, color: TONES.accent, fontFamily: MONO }}
          >
            line {line}
          </span>
        )}
        <span>{narration}</span>
      </p>
    </div>
  );
}

const BTN =
  'inline-flex size-9 items-center justify-center rounded-lg border transition-colors disabled:opacity-35';

export function TransportBar({
  index,
  total,
  playing,
  speed,
  atStart,
  atEnd,
  onToggle,
  onNext,
  onPrev,
  onRestart,
  onSeek,
  onSpeed,
}: {
  index: number;
  total: number;
  playing: boolean;
  speed: Speed;
  atStart: boolean;
  atEnd: boolean;
  onToggle: () => void;
  onNext: () => void;
  onPrev: () => void;
  onRestart: () => void;
  onSeek: (index: number) => void;
  onSpeed: (speed: Speed) => void;
}) {
  const cycleSpeed = () => {
    const i = SPEEDS.indexOf(speed);
    onSpeed(SPEEDS[(i + 1) % SPEEDS.length]);
  };

  return (
    <div
      className="flex items-center gap-2 rounded-2xl border px-3 py-2.5 sm:gap-3"
      style={{ backgroundColor: CANVAS.panel, borderColor: CANVAS.panelBorder }}
    >
      <button
        type="button"
        onClick={onRestart}
        disabled={atStart && !playing}
        aria-label="Restart from the first step"
        className={BTN}
        style={{ borderColor: CANVAS.panelBorder, color: CANVAS.textDim }}
      >
        <RotateCcw className="size-4" />
      </button>
      <button
        type="button"
        onClick={onPrev}
        disabled={atStart}
        aria-label="Previous step"
        className={`${BTN} hidden sm:inline-flex`}
        style={{ borderColor: CANVAS.panelBorder, color: CANVAS.textDim }}
      >
        <SkipBack className="size-4" />
      </button>
      <button
        type="button"
        onClick={onToggle}
        aria-label={playing ? 'Pause' : 'Play'}
        className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl text-[#1c1917] transition-opacity hover:opacity-90"
        style={{ backgroundColor: TONES.accent }}
      >
        {playing ? <Pause className="size-[18px]" /> : <Play className="size-[18px]" />}
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={atEnd}
        aria-label="Next step"
        className={BTN}
        style={{ borderColor: CANVAS.panelBorder, color: CANVAS.textDim }}
      >
        <SkipForward className="size-4" />
      </button>

      <input
        type="range"
        min={0}
        max={Math.max(total - 1, 0)}
        value={index}
        onChange={(e) => onSeek(Number(e.target.value))}
        aria-label={`Step ${index + 1} of ${total}`}
        className="kp-viz-range h-1.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full"
        style={{
          // Filled portion up to the thumb, track colour after it.
          background: `linear-gradient(to right, ${TONES.accent} 0%, ${TONES.accent} ${
            total > 1 ? (index / (total - 1)) * 100 : 0
          }%, ${CANVAS.panelBorder} ${total > 1 ? (index / (total - 1)) * 100 : 0}%, ${CANVAS.panelBorder} 100%)`,
        }}
      />

      <button
        type="button"
        onClick={cycleSpeed}
        aria-label={`Playback speed ${speed}×, click to change`}
        className="shrink-0 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold tabular-nums"
        style={{ borderColor: CANVAS.panelBorder, color: CANVAS.textDim, fontFamily: MONO }}
      >
        {speed}×
      </button>
    </div>
  );
}
